import { Component, NgZone, OnDestroy, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { AwsRum, AwsRumConfig } from 'aws-rum-web';
import { environment } from '../environments/environment';
import { ServiceWorkerService } from './shared/service-worker/service-worker.service';
import { OfflineService } from './services/offline.service';
import { NavigationEnd, Router } from '@angular/router';
import { filter, Subscription } from 'rxjs';
import { isGoogleTranslateError } from './utils/url-security.utils';
import { BreadcrumbService } from './common/service/breadcrumb.service';
import { BreadcrumbModel } from './common/model/breadcrumb.model';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
    routerSubscription: Subscription | undefined;
    breadcrumbSubscription: Subscription | undefined;

    private readonly EXCLUDED_PATH = '/public/upload';
    private readonly APP_TITLE_SUFFIX = 'ePRINTit - Secure Cloud Solution';

    constructor(private workerService: ServiceWorkerService, private ngZone: NgZone, private offlineService: OfflineService,
                private router: Router, private titleService: Title, private breadcrumbService: BreadcrumbService,
                private translateService: TranslateService) {
        //this.workerService.checkForUpdates();

        // 1. GLOBAL EVENT LISTENER for PWA INSTALL PROMPT
        window.addEventListener('beforeinstallprompt', this.handleInstallPrompt);

        window.addEventListener('error', (event) => {
            if (event?.message?.includes('Cannot read properties of undefined')) {
                console.warn('Suppressed Google Translate error:', event.message);
                event.preventDefault();
                return false;
            }
        }, true);


        this.ngZone.runOutsideAngular(() => {
            window.addEventListener('error', (event) => {
                if (event?.message?.includes('Uncaught TypeError: Cannot read properties of undefined')) {
                    event.preventDefault();
                    return true;
                }
            });

            window.addEventListener('unhandledrejection', (event) => {
                if (event.reason && isGoogleTranslateError(event.reason)) {
                    event.preventDefault();
                } else {
                    console.warn('[Outside Angular] Unhandled Rejection:', event.reason);
                }
            });
        });
    }

    handleInstallPrompt = (event: Event): void => {
        const isUploadRoute = window.location.pathname.startsWith(this.EXCLUDED_PATH);

        // Only cancel the install prompt on the excluded route
        if (isUploadRoute) {
            event.preventDefault();
        }
    };



    ngOnInit(): void {
        this.breadcrumbSubscription = this.breadcrumbService.breadcrumbs$.subscribe((breadcrumbs: BreadcrumbModel[]) => {
            if(this.router.url === '/public/upload'){
                this.titleService.setTitle(`Upload | ${this.APP_TITLE_SUFFIX}`);
                return;
            }
            const lastBreadcrumb = breadcrumbs[breadcrumbs.length - 1];
            const breadcrumbName = lastBreadcrumb?.name?.trim();
            const pageTitle = this.translateService.instant(breadcrumbName);
            this.titleService.setTitle(`${pageTitle} | ${this.APP_TITLE_SUFFIX}`);
        });

        this.routerSubscription = this.router.events
            .pipe(filter(event => event instanceof NavigationEnd)).subscribe((event: NavigationEnd) => {
                this.updateManifestLink(event.urlAfterRedirects);
            });

        try {
            const config: AwsRumConfig = {
                sessionSampleRate: 0.1,
                guestRoleArn: environment.guestRoleArn,
                identityPoolId: environment.identityPoolId,
                endpoint: 'https://dataplane.rum.us-east-1.amazonaws.com',
                telemetries: ['performance', 'errors', 'http'],
                allowCookies: true,
                enableXRay: false
            };

            const APPLICATION_ID: string = environment.applicationId;
            const APPLICATION_VERSION: string = '1.0.0';
            const APPLICATION_REGION: string = 'us-east-1';

            const awsRum: AwsRum = new AwsRum(
                APPLICATION_ID,
                APPLICATION_VERSION,
                APPLICATION_REGION,
                config
            );
        } catch (error) {
            console.error(error);
        }

        const scriptElement = document.getElementById('monerisScript') as HTMLScriptElement;
        scriptElement.src = environment.monerisScriptSource;
    }

    updateManifestLink(url: string): void {
        const isExcluded = url.startsWith(this.EXCLUDED_PATH);
        const manifestLink = document.querySelector('link[rel="manifest"]');

        if (isExcluded) {
            if (manifestLink) {
                manifestLink.remove();
            }
        } else {
            if (!manifestLink) {
                this.addManifestLink();
            }
        }
    }

    addManifestLink(): void {
        const link = document.createElement('link');
        link.rel = 'manifest';
        link.href = 'manifest.webmanifest';
        document.head.appendChild(link);
    }

    ngOnDestroy(): void {
        window.removeEventListener('beforeinstallprompt', this.handleInstallPrompt);

        if (this.breadcrumbSubscription) {
            this.breadcrumbSubscription.unsubscribe();
        }

        if (this.routerSubscription) {
            this.routerSubscription.unsubscribe();
        }
    }

}
